import { clamp01, lerp, easeOutCubic, easeInCubic } from "./easing";

/**
 * Anivaze v2 — renderer-level scene motion (Phase-3 "lite").
 *
 * Turns a node's local playback time into a real, eased entrance → reveal →
 * hold → exit, applied at the group level so it works on ALL templates without
 * per-template changes. This replaces the v1 "static template + meaningless
 * constant zoom + white flash" with motion that reads as intentional animation.
 *
 * Pure function of (localTime, duration) → deterministic, so it drives the live
 * player and (later) frame-accurate export identically.
 */
export interface SceneMotion {
  /** Group opacity (fade in, fade out). */
  opacity: number;
  /** Group scale — gentle settle on enter, slight shrink on exit. */
  scale: number;
  /** Vertical offset in px — slides up into place, drops slightly on exit. */
  translateY: number;
  /**
   * Radius of the radial "bloom" reveal clip (canvas center). The scene draws
   * itself in from the middle instead of popping fully formed. `null` once the
   * reveal has fully covered the canvas (no clipping needed).
   */
  revealRadius: number | null;
}

const ENTER_S = 0.6; // entrance settle duration
const REVEAL_S = 0.95; // radial bloom duration
const EXIT_S = 0.45; // exit duration
// Distance center(400,225) → corner ≈ 459px; 560 fully covers the 800×450 canvas.
const FULL_RADIUS = 560;

export function computeSceneMotion(localTime: number, duration: number): SceneMotion {
  const enter = easeOutCubic(clamp01(localTime / ENTER_S));
  const exit = easeInCubic(clamp01((localTime - (duration - EXIT_S)) / EXIT_S));
  const revealT = clamp01(localTime / REVEAL_S);

  return {
    opacity: enter * (1 - exit),
    scale: lerp(0.96, 1, enter) * (1 - 0.04 * exit),
    translateY: lerp(14, 0, enter) + 10 * exit,
    revealRadius: revealT < 1 ? lerp(70, FULL_RADIUS, easeOutCubic(revealT)) : null,
  };
}
